<template>
  <div class="opportunity-estimation">
    <OpportunityHeader />
    <BaseLoadingBar v-if="isPending" />

    <div v-else>
      <div class="step-indicators">
        <div class="step-indicator">
          <div class="step-line" :class="{ active: currentStep === 1, disabled: hasExtractions }"></div>
          <div class="step-content">
            <div
              :class="[
                'step-circle',
                { active: currentStep === 1, disabled: !hasExtractions },
                { checked: currentStep === 1 && !hasExtractions },
              ]"
            >
              <div class="opportunity-estimation__check-circle-container" v-if="hasExtractions">
                <BaseIcon name="check" class="opportunity-estimation__icon" />
              </div>
            </div>
            <div class="step-name">{{ $t('estimation.generation') }}</div>
          </div>
        </div>
        <div class="step-indicator">
          <div class="step-line" :class="{ active: currentStep === 2, disabled: hasExtractions }"></div>
          <div class="step-content">
            <div
              :class="[
                'step-circle',
                { active: currentStep === 2, disabled: !hasExtractions },
                { checked: currentStep === 2 && hasExtractions },
              ]"
            >
              <div class="opportunity-estimation__check-circle-container" v-if="hasExtractions">
                <BaseIcon name="check" class="opportunity-estimation__icon" />
              </div>
            </div>
            <div class="step-name">{{ $t('estimation.download') }}</div>
          </div>
        </div>
      </div>

      <EstimationGeneration v-if="currentStep === 1" :opportunityId="opportunityId" />
      <EstimationDocuments v-else-if="currentStep === 2" :opportunityId="opportunityId" />
    </div>
  </div>
</template>

<script>
import EstimationGeneration from '@/components/page-opportunity-estimation/EstimationGeneration.vue';
import EstimationDocuments from '@/components/page-opportunity-estimation/EstimationDocuments.vue';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseIcon from '@/components/base/BaseIcon';
import OpportunityHeader from '@/components/page-opportunities/OpportunityHeader';

import { mapActions, mapGetters } from 'vuex';
import { paths } from '@/util/paths';

export default {
  components: {
    EstimationGeneration,
    EstimationDocuments,
    BaseIcon,
    BaseLoadingBar,
    OpportunityHeader,
  },
  props: {
    opportunityId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      interval: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenant', ['tenant']),
    ...mapGetters('tenantModules', ['tenantModules']),
    ...mapGetters('tenantModules', { tenantModulesPending: 'pending' }),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('opportunity', { opportunityPending: 'pending' }),
    ...mapGetters('productBundleExtractions', ['productBundleExtractions', 'pending']),
    isPending() {
      return this.tenantModulesPending || this.opportunityPending || this.pending;
    },
    hasExtractions() {
      return this.productBundleExtractions.length > 0;
    },
    currentStep() {
      return this.hasExtractions ? 2 : 1;
    },
  },
  methods: {
    ...mapActions('opportunity', ['getOpportunity']),
    ...mapActions('productBundleExtractions', ['searchProductBundleExtractions']),
    ...mapActions('productBundleExtraction', ['getProductBundleExtractionById']),
    getProductBundleUntilCompleted(id) {
      const checkStatus = () => {
        this.getProductBundleExtractionById({ id })
          .then((extraction) => {
            if (extraction && extraction.status !== 'COMPLETED' && extraction.status !== 'ERROR') {
              // Schedule the next check if the status is not COMPLETED or ERROR
              this.interval = setTimeout(checkStatus, 15000);
            }
          })
          .catch((error) => {
            console.error(`Error fetching product bundle extraction: ${error}`);
          });
      };

      // Start the first status check
      checkStatus();
    },
    handleError(error) {
      if (!error.response) this.$router.replace(paths.notFound);

      // If a not found or forbidden error occurs, redirect to not-found page
      if (error.response.status === 404 || error.response.status === 403) {
        this.$router.replace(paths.notFound);
      } else {
        // Otherwise show an error toast
        console.error(error);
        this.$toast.error(error);
        throw error;
      }
    },
  },
  async created() {
    try {
      const opportunityId = this.opportunityId;

      if (!this.opportunity || this.opportunity.id !== opportunityId) {
        const opportunity = this.getOpportunity({ id: opportunityId });
        if (!opportunity) return;
      }

      this.searchProductBundleExtractions({
        authorId: null,
        opportunityId: [opportunityId],
        opportunityDocumentId: null,
        status: null,
      });
    } catch (error) {
      this.handleError(error);
    }
  },
  beforeDestroy() {
    clearTimeout(this.interval);
  },
  watch: {
    productBundleExtractions() {
      if (!this.hasExtractions) return;
      this.getProductBundleUntilCompleted(this.productBundleExtractions[0].id);
    },
  },
};
</script>

<style lang="scss">
.opportunity-estimation {
  &__check-circle-container {
    height: 14px;
    width: 14px;
    border-radius: $border-radius-round;
    display: flex;
    justify-content: center;
    align-items: center;
    .base-icon {
      height: 14px;
      width: 14px;
    }
  }
  .step-indicators {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 30px;
    position: relative;
  }

  .step-indicator {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-right: 10px;
  }

  .step-content {
    display: flex;
    align-items: center;
    padding-top: 5px;
    width: 200px;
  }

  .step-circle {
    width: 16px;
    height: 16px;
    border-radius: $border-radius-round;
    border: 2px solid $color-slate-300;
    position: relative;
    margin-right: 10px;
  }

  .step-circle.active {
    background-color: $color-accent-500;
    border: 2px solid $color-accent-500;
  }

  .step-circle.active::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: $border-radius-round;
    background-color: transparent;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .step-name {
    font-weight: bold;
  }

  .step-indicators::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background-color: $color-accent-500;
    z-index: -1;
  }

  .step-line {
    height: 3px;
    width: 100%;
    background-color: $color-slate-300;
    transition: width 0.3s ease-in-out, left 0.3s ease-in-out;
    border-radius: $border-radius-large;
  }

  .step-line.active {
    background-color: $color-accent-500;
  }

  .step-indicator.disabled .step-line {
    background-color: $color-slate-300;
  }

  .disabled {
    pointer-events: none;
  }
}
</style>
